// /app/brands/$brandId — Brand Studio with tabbed sections.
//
// Tab state lives in the URL hash (#general, #voice, …) so refresh + back +
// shareable links work. Each tab loads its own data on mount; switching
// tabs is instant since panels are tiny.

import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { Select } from "../../../components/Select";
import { useI18n } from "../../../lib/i18n";
import { ApiError, brands, type Brand, type BrandKB } from "../../../lib/api";
import { COUNTRIES, LANGUAGES, TIMEZONES } from "../../../lib/brand-options";
import { CATEGORIES, INDUSTRIES, suggestionsFor } from "../../../lib/industry-options";
import {
  BRAND_TAB_EVENT,
  tabFromHash,
  type BrandTabKey,
} from "../../../components/brand/Tabs";
import { VoicePanel } from "../../../components/brand/VoicePanel";
import { AudiencePanel } from "../../../components/brand/AudiencePanel";
import { ProductsPanel } from "../../../components/brand/ProductsPanel";
import { ServicesPanel } from "../../../components/brand/ServicesPanel";
import { TopicsPanel } from "../../../components/brand/TopicsPanel";
import { ExamplesPanel } from "../../../components/brand/ExamplesPanel";
import { TemplatesPanel } from "../../../components/brand/TemplatesPanel";
import { IntegrationsPanel } from "../../../components/brand/IntegrationsPanel";
import { AIAssistCard } from "../../../components/brand/AIAssistCard";
import { KitPanel } from "../../../components/brand/KitPanel";
import { publishBrandToContextCache } from "../../../components/BrandContextBar";

const ALL_TABS: BrandTabKey[] = [
  "general", "voice", "audience", "products", "services", "topics", "examples", "templates", "integrations", "kit", "knowledge",
];

function BrandDetail() {
  const { t } = useI18n();
  const { brandId } = Route.useParams();

  const [b, setB] = useState<Brand | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [tab, setTab] = useState<BrandTabKey>(() => tabFromHash("general", ALL_TABS));

  useEffect(() => {
    let cancelled = false;
    brands
      .get(brandId)
      .then((row) => {
        if (cancelled) return;
        setB(row);
        // Share the freshly-loaded brand with the shell's context bar so
        // it doesn't have to re-fetch.
        publishBrandToContextCache(row);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) setLoadErr("not_found");
        else setLoadErr(t("common.errorGeneric"));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Sidebar tab clicks dispatch a custom event via changeBrandTab().
  // We listen here and update the tab state directly. This bypasses
  // TanStack Router's hash-only navigation, which doesn't reliably
  // trigger a re-render of this page.
  //
  // Also still respond to the router hash for cases where the user
  // hits back/forward or pastes a URL with a hash directly.
  const routerHash = useRouterState({ select: (s) => s.location.hash });
  useEffect(() => {
    setTab(tabFromHash("general", ALL_TABS));
  }, [routerHash]);
  useEffect(() => {
    const onTab = (e: Event) => {
      const detail = (e as CustomEvent).detail as BrandTabKey | undefined;
      if (detail && ALL_TABS.includes(detail)) setTab(detail);
    };
    window.addEventListener(BRAND_TAB_EVENT, onTab);
    return () => window.removeEventListener(BRAND_TAB_EVENT, onTab);
  }, []);

  if (loadErr === "not_found") {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <Card>
          <span className="eyebrow">404</span>
          <h2 className="font-display text-xl font-semibold mt-2">{t("notfound.title")}</h2>
          <Link to="/app/brands" className="text-secondary hover:text-primary text-sm mt-4 inline-block">
            ← {t("brands.title")}
          </Link>
        </Card>
      </div>
    );
  }
  if (loadErr) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <Card><p className="text-error">{loadErr}</p></Card>
      </div>
    );
  }
  if (!b) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <Card><p className="text-secondary">{t("common.loading")}</p></Card>
      </div>
    );
  }

  // Header + tabs now live in the shell's BrandContextBar (rendered by
  // app/route.tsx). This page only renders the panel content.
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-6">
      <div className="flex flex-col gap-6 min-w-0">
        {tab === "general" && <GeneralPanel brand={b} onUpdated={setB} />}
        {tab === "voice" && <VoicePanel brandId={brandId} />}
        {tab === "audience" && <AudiencePanel brandId={brandId} />}
        {tab === "products" && <ProductsPanel brandId={brandId} />}
        {tab === "services" && <ServicesPanel brandId={brandId} />}
        {tab === "topics" && <TopicsPanel brandId={brandId} />}
        {tab === "examples" && <ExamplesPanel brandId={brandId} />}
        {tab === "templates" && <TemplatesPanel brandId={brandId} />}
        {tab === "integrations" && <IntegrationsPanel brandId={brandId} />}
        {tab === "kit" && <KitPanel brandId={brandId} />}
        {tab === "knowledge" && <KBPanel brandId={brandId} />}
      </div>
    </div>
  );
}

// ============================================================
// General panel — the legacy summary/edit form, kept inline since
// it's the only one that mutates the parent brand state.
// ============================================================

function GeneralPanel({ brand: b, onUpdated }: { brand: Brand; onUpdated: (b: Brand) => void }) {
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [legalName, setLegalName] = useState(b.legal_name);
  const [tradeName, setTradeName] = useState(b.trade_name);
  const [slug, setSlug] = useState(b.slug);
  const [country, setCountry] = useState(b.country);
  const [primaryLanguage, setPrimaryLanguage] = useState(b.primary_language);
  const [timezone, setTimezone] = useState(b.timezone);
  const [industry, setIndustry] = useState(b.industry ?? "");
  const [category, setCategory] = useState(b.category ?? "");
  const [commentsMode, setCommentsMode] = useState<"approval" | "auto">(b.comments_mode);
  // Telegram channel — kept as a plain string so the input can be empty
  // (= clear), "@channel", or a numeric chat id. Server validates shape.
  const [telegramChannel, setTelegramChannel] = useState<string>(b.telegram_channel ?? "");

  function cancelEdit() {
    setLegalName(b.legal_name); setTradeName(b.trade_name); setSlug(b.slug);
    setCountry(b.country); setPrimaryLanguage(b.primary_language); setTimezone(b.timezone);
    setIndustry(b.industry ?? ""); setCategory(b.category ?? "");
    setCommentsMode(b.comments_mode);
    setTelegramChannel(b.telegram_channel ?? "");
    setSaveErr(null);
    setEditing(false);
  }

  async function save() {
    setSaving(true); setSaveErr(null);
    try {
      const patch: Parameters<typeof brands.patch>[1] = {};
      if (legalName !== b.legal_name) patch.legal_name = legalName.trim();
      if (tradeName !== b.trade_name) patch.trade_name = tradeName.trim();
      if (slug !== b.slug) patch.slug = slug;
      if (country !== b.country) patch.country = country;
      if (primaryLanguage !== b.primary_language) patch.primary_language = primaryLanguage;
      if (timezone !== b.timezone) patch.timezone = timezone;
      if ((industry || "") !== (b.industry ?? "")) patch.industry = industry;
      if ((category || "") !== (b.category ?? "")) patch.category = category;
      if (commentsMode !== b.comments_mode) patch.comments_mode = commentsMode;
      // Telegram channel: only include in patch when changed. Empty
      // string is a real value (= clear it), distinct from "no patch".
      const tgCurrent = b.telegram_channel ?? "";
      if (telegramChannel.trim() !== tgCurrent) patch.telegram_channel = telegramChannel.trim();
      if (Object.keys(patch).length === 0) { setEditing(false); return; }
      const updated = await brands.patch(b.id, patch);
      onUpdated(updated);
      setEditing(false);
      setSavedToast(true);
      window.setTimeout(() => setSavedToast(false), 2000);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setSaveErr(t("brands.slugTaken"));
      else setSaveErr(t("common.errorGeneric"));
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    try {
      await brands.delete(b.id);
      void nav({ to: "/app/brands", replace: true });
    } catch {
      setSaveErr(t("common.errorGeneric"));
      setConfirmingDelete(false);
    }
  }

  // After AI applies brand basics (industry/category), refresh from the
  // server so the displayed summary reflects the new values.
  async function refreshBrand() {
    try {
      const fresh = await brands.get(b.id);
      onUpdated(fresh);
    } catch {
      /* non-fatal — operator can refresh manually */
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* AI Assist — top of the General tab so a new brand sees it first. */}
      <AIAssistCard brand={b} onApplied={() => void refreshBrand()} />

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">{t("brands.detail.summary")}</h2>
          <div className="flex items-center gap-3">
            {savedToast ? <span className="text-success text-sm" role="status">✓ {t("brands.detail.saved")}</span> : null}
            {!editing ? <Button variant="secondary" onClick={() => setEditing(true)}>{t("brands.detail.edit")}</Button> : null}
          </div>
        </div>

        {editing ? (
          <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="flex flex-col gap-4" noValidate>
            <Input label={t("brands.field.legalName")} value={legalName} onChange={(e) => setLegalName(e.target.value)} maxLength={200} />
            <Input label={t("brands.field.tradeName")} value={tradeName} onChange={(e) => setTradeName(e.target.value)} maxLength={200} />
            <Input label={t("brands.field.slug")} value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              maxLength={32} hint={t("brands.field.slugHint")} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Select label={t("brands.field.country")} value={country} onChange={(e) => setCountry(e.target.value)} options={COUNTRIES} />
              <Select label={t("brands.field.primaryLanguage")} value={primaryLanguage} onChange={(e) => setPrimaryLanguage(e.target.value)} options={LANGUAGES} />
            </div>
            <Select label={t("brands.field.timezone")} value={timezone} onChange={(e) => setTimezone(e.target.value)} options={TIMEZONES} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label={t("brands.field.industry")} value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                suggestions={suggestionsFor(INDUSTRIES, locale)} autoComplete="off" />
              <Input label={t("brands.field.category")} value={category}
                onChange={(e) => setCategory(e.target.value)}
                suggestions={suggestionsFor(CATEGORIES, locale)} autoComplete="off" />
            </div>
            <Select label={t("brands.field.commentsMode")} value={commentsMode}
              onChange={(e) => setCommentsMode(e.target.value as "approval" | "auto")}
              options={[
                { value: "approval", label: t("brands.field.commentsMode.approval") },
                { value: "auto", label: t("brands.field.commentsMode.auto") },
              ]} />
            <Input
              label={t("brands.field.telegramChannel")}
              value={telegramChannel}
              onChange={(e) => setTelegramChannel(e.target.value)}
              placeholder="@my_brand_news"
              hint={t("brands.field.telegramChannelHint")}
              maxLength={64}
              autoComplete="off"
            />
            {saveErr ? <p className="text-error text-sm" role="alert">{saveErr}</p> : null}
            <div className="flex items-center justify-end gap-3 mt-2">
              <Button variant="ghost" type="button" onClick={cancelEdit} disabled={saving}>{t("brands.detail.cancel")}</Button>
              <Button variant="primary" type="submit" loading={saving}>{t("brands.detail.save")}</Button>
            </div>
          </form>
        ) : (
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
            <Row label={t("brands.field.legalName")} value={b.legal_name} />
            <Row label={t("brands.field.country")} value={`${b.country} · ${b.primary_language}`} />
            <Row label={t("brands.field.timezone")} value={b.timezone} />
            <Row label={t("brands.field.commentsMode")} value={b.comments_mode === "approval"
              ? t("brands.field.commentsMode.approval")
              : t("brands.field.commentsMode.auto")} />
            {b.industry ? <Row label={t("brands.field.industry")} value={b.industry} /> : null}
            {b.category ? <Row label={t("brands.field.category")} value={b.category} /> : null}
            {b.telegram_channel ? <Row label={t("brands.field.telegramChannel")} value={b.telegram_channel} /> : null}
          </dl>
        )}
      </Card>

      <Card>
        {!confirmingDelete ? (
          <Button variant="secondary" onClick={() => setConfirmingDelete(true)}>{t("brands.detail.delete")}</Button>
        ) : (
          <div className="flex flex-col gap-3">
            <h3 className="font-display text-base font-semibold text-error">{t("brands.detail.deleteConfirm.title")}</h3>
            <p className="text-sm text-secondary">{t("brands.detail.deleteConfirm.body")}</p>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>{t("brands.detail.deleteConfirm.cancel")}</Button>
              <Button variant="primary" onClick={() => void doDelete()} className="!bg-error hover:!bg-error/80">
                {t("brands.detail.deleteConfirm.confirm")}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// Kit + Knowledge panels — read-only summaries (full editors will
// be deferred to v0.2.1; the data already exists so we display it).
// ============================================================

function KBPanel({ brandId }: { brandId: string }) {
  const { t } = useI18n();
  const [kb, setKb] = useState<BrandKB | null | "missing">(null);
  useEffect(() => {
    let cancelled = false;
    brands.getKB(brandId)
      .then((k) => !cancelled && setKb(k))
      .catch(() => !cancelled && setKb("missing"));
    return () => { cancelled = true; };
  }, [brandId]);
  if (kb === null) return <Card><p className="text-secondary text-sm">{t("common.loading")}</p></Card>;
  if (kb === "missing") return <Card><p className="text-secondary text-sm">{t("brands.detail.kb.empty")}</p></Card>;
  const color =
    kb.status === "ready" ? "text-success"
      : kb.status === "failed" ? "text-error"
      : (kb.status === "classifying" || kb.status === "clarifying") ? "text-warning"
      : "text-info";
  return (
    <Card>
      <h2 className="font-display text-lg font-semibold mb-3">{t("brands.detail.kb.title")}</h2>
      <div className="flex items-center gap-2 text-xs mb-3">
        <span className="font-mono uppercase tracking-[0.16em] text-muted">{t("brands.detail.kb.status")}:</span>
        <span className={`font-mono text-xs uppercase ${color}`}>{kb.status}</span>
      </div>
      {kb.business_description ? <p className="text-sm text-primary">{kb.business_description}</p>
        : <p className="text-sm text-secondary">{t("brands.detail.kb.empty")}</p>}
      {kb.key_differentiators?.length ? (
        <ul className="text-sm text-secondary list-disc pl-5 mt-3">
          {kb.key_differentiators.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      ) : null}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">{label}</dt>
      <dd className="text-primary">{value}</dd>
    </div>
  );
}

export const Route = createFileRoute("/app/brands/$brandId")({
  component: BrandDetail,
});
